import { StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    borderWidth: 1,
    backgroundColor: "#F5F6F9",
    borderColor: "#E6E8ED",
    borderRadius: 12,
    padding: 8,
    marginBottom: 8,
    color: "#0A1A53",
  },
  containerWithoutBorder: {
    flexDirection: "row",
    padding: 8,
    marginBottom: 8,
    color: "#0A1A53",
  },
  iconWrapper: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  title: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#0A1A53",
  },
  leftColumn: {
    width: "20%",
    justifyContent: "center",
    alignItems: "flex-start",
    paddingRight: 8,
  },
  leftHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  leftTitle: {
    fontSize: 12,
    fontWeight: "bold",
  },
  rightColumn: {
    width: "80%",
    paddingLeft: 12,
    gap: 6,
  },
  firstRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  statItem: {
    width: "33%",
    fontSize: 9,
  },
  description: {
    fontSize: 9,
  },

  statsRow: {
    flexDirection: "row",
    marginBottom: 8,
  },
  statText: {
    fontSize: 9,
    color: "#0A1A53",
  },
  statTextLow: {
    fontSize: 9,
    color: "#AFD1AD",
  },
  statTextMedium: {
    fontSize: 9,
    color: "#EEC9A2",
  },
  statTextHigh: {
    fontSize: 9,
    color: "#F6A890",
  },
  barContainer: {
    flexDirection: "row",
    height: 15,
    borderRadius: 12,
    overflow: "hidden",
  },
  barSegment: {
    height: 15,
  },
});

type IconWrapperProps = {
  Icon: React.ComponentType<{ x?: number; y?: number }>;
};

const IconWrapper = ({ Icon }: IconWrapperProps) => {
  return (
    <View style={styles.iconWrapper}>
      <Svg width={20} height={20}>
        <Icon x={0} y={0} />
      </Svg>
    </View>
  );
};

type RiskDistributionBlockProps = {
  title: string;
  icon?: React.ComponentType<{ x?: number; y?: number }>;
  lowRisk: { count: number; percentage: number };
  mediumRisk: { count: number; percentage: number };
  highRisk: { count: number; percentage: number };
  description: ReactNode;
  withBorder?: boolean;
};

export const RiskDistributionBlock = ({
  title,
  icon: Icon,
  lowRisk,
  mediumRisk,
  highRisk,
  description,
  withBorder = false,
}: RiskDistributionBlockProps) => {
  const { t } = useTranslation();

  // Normaliser les pourcentages pour qu'ils soient des entiers et totalisent 100
  // Utilise la méthode du plus grand reste (largest remainder method)
  const normalizePercentages = (
    low: number,
    medium: number,
    high: number
  ): { low: number; medium: number; high: number } => {
    const values = [
      { original: low, floored: Math.floor(low), remainder: low - Math.floor(low), index: 0 },
      {
        original: medium,
        floored: Math.floor(medium),
        remainder: medium - Math.floor(medium),
        index: 1,
      },
      { original: high, floored: Math.floor(high), remainder: high - Math.floor(high), index: 2 },
    ];

    // Calculer la somme des parties entières
    const flooredSum = values.reduce((sum, v) => sum + v.floored, 0);
    const difference = 100 - flooredSum;

    // Trier par reste décroissant pour distribuer la différence
    const sorted = [...values].sort((a, b) => b.remainder - a.remainder);

    // Distribuer la différence aux valeurs avec les plus grands restes
    const result = [0, 0, 0];
    for (let i = 0; i < 3; i++) {
      result[sorted[i].index] = sorted[i].floored + (i < difference ? 1 : 0);
    }

    return { low: result[0], medium: result[1], high: result[2] };
  };

  const normalized = normalizePercentages(
    lowRisk.percentage,
    mediumRisk.percentage,
    highRisk.percentage
  );

  // Calculer les largeurs en pourcentage pour chaque segment
  const totalPercentage = normalized.low + normalized.medium + normalized.high;
  const lowWidth = totalPercentage > 0 ? (normalized.low / totalPercentage) * 100 : 0;
  const mediumWidth = totalPercentage > 0 ? (normalized.medium / totalPercentage) * 100 : 0;
  const highWidth = totalPercentage > 0 ? (normalized.high / totalPercentage) * 100 : 0;

  return (
    <View style={withBorder ? styles.container : styles.containerWithoutBorder}>
      {/* Colonne gauche (20%) */}
      <View style={styles.leftColumn}>
        <View style={styles.leftHeaderRow}>
          {Icon && <IconWrapper Icon={Icon} />}
          <Text style={styles.title}>{title}</Text>
        </View>
      </View>

      {/* Colonne droite (80%) */}
      <View style={styles.rightColumn}>
        {/* Ligne 1 : stats */}
        <View style={styles.firstRow}>
          <Text style={styles.statTextLow}>
            {t("pdf.validatedFolders.sections.riskDistribution.low", {
              count: lowRisk.count,
              percentage: normalized.low,
            })}
          </Text>

          <Text style={styles.statTextMedium}>
            {t("pdf.validatedFolders.sections.riskDistribution.medium", {
              count: mediumRisk.count,
              percentage: normalized.medium,
            })}
          </Text>

          <Text style={styles.statTextHigh}>
            {t("pdf.validatedFolders.sections.riskDistribution.high", {
              count: highRisk.count,
              percentage: normalized.high,
            })}
          </Text>
        </View>

        {/* Ligne 2 : barre */}
        <View>
          <View style={styles.barContainer}>
            <View
              style={[
                styles.barSegment,
                {
                  width: `${lowWidth}%`,
                  backgroundColor: "#AFD1AD",
                },
              ]}
            />
            <View
              style={[
                styles.barSegment,
                {
                  width: `${mediumWidth}%`,
                  backgroundColor: "#EEC9A2",
                },
              ]}
            />
            <View
              style={[
                styles.barSegment,
                {
                  width: `${highWidth}%`,
                  backgroundColor: "#F6A890",
                },
              ]}
            />
          </View>
        </View>

        {/* Ligne 3 : description */}
        <Text style={styles.description}>{description}</Text>
      </View>
    </View>
  );
};
